/*
 * @Author: Ryan
 * @Date: 2026-03-11
 * @Description: Frequently Asked Questions section for AI Twin Generator
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`border border-[#CDCDCD] transition-all duration-300 cursor-pointer ${
        isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'
      }`}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        <div className="flex items-center justify-between gap-4">
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        <div
          className={`overflow-hidden transition-all duration-300 ${
            isExpanded ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'What is an AI twin?',
      answer: (
        <p>
          An AI twin is a digital version of you that replicates your appearance, voice,
          and on-screen delivery. It allows you to generate talking videos without recording
          yourself each time, saving significant time and effort. The AI twin speaks your
          script with natural lip sync and expressive facial movement, creating a realistic
          on-camera presence. This makes it ideal for creators, marketers, and teams who
          want consistent video content at scale.
        </p>
      ),
    },
    {
      id: 'faq-2',
      question: 'How do I create my AI twin?',
      answer: (
        <ol className="list-decimal list-inside space-y-2">
          <li>
            Upload a high-quality photo of yourself recorded in bright, even lighting to
            ensure accurate facial capture.
          </li>
          <li>
            Type the lines you want your AI twin to deliver, using any tone or style that
            fits your content.
          </li>
          <li>
            Choose from a range of AI voices or upload your own audio for a more authentic
            and personalized result.
          </li>
          <li>
            FalcoCut combines your avatar, script, and voice to produce a polished talking
            video in just a few minutes.
          </li>
        </ol>
      ),
    },
    {
      id: 'faq-3',
      question:
        "What's the difference between an AI twin and a stock AI persona?",
      answer: (
        <p>
          An AI twin is a personalized avatar built from your own image or video, making it
          unique to you. A stock AI persona is a pre-made character designed for general use
          and not tied to any specific individual. Stock personas are great for quick testing
          or generic content, but they cannot represent your identity or brand. An AI twin
          offers authenticity, consistency, and a recognizable presence across all your
          videos.
        </p>
      ),
    },
    {
      id: 'faq-4',
      question: 'Can an AI twin speak multiple languages?',
      answer: (
        <p>
          Yes. FalcoCut supports multilingual speech generation, allowing your AI twin to
          speak in many languages with accurate lip sync. This makes it easy to create
          global content without needing separate recordings or translations. The system
          adapts your script into the selected language while maintaining natural pacing and
          tone. It&apos;s a powerful way to reach international audiences with personalized
          video messages.
        </p>
      ),
    },
    {
      id: 'faq-5',
      question: 'Where can I use my AI twin?',
      answer: (
        <p>
          Your AI twin can be used across social media, marketing campaigns, training
          materials, and internal communications. It works well for product demos,
          announcements, tutorials, and personalized outreach videos. Because it delivers
          consistent quality, it helps teams scale content without scheduling shoots or
          appearing on camera. You can generate videos for any platform that supports
          standard video formats.
        </p>
      ),
    },
    {
      id: 'faq-6',
      question: 'How long does it take to create an AI twin?',
      answer: (
        <p>
          Creating your AI twin typically takes only a few minutes from upload to
          completion. The system processes your photo or video to capture your facial
          structure, expressions, and overall look. Once your twin is ready, generating new
          talking videos usually takes about a minute per script. This fast workflow allows
          you to produce high-quality content whenever you need it.
        </p>
      ),
    },
    {
      id: 'faq-7',
      question: 'Is the AI twin generator free?',
      answer: (
        <p>
          FalcoCut offers a free plan that lets you explore the platform and try essential
          AI features. Free users can generate basic videos with limited usage and standard
          output quality. Paid plans unlock higher generation limits, advanced capabilities,
          and watermark-free exports for professional use. This flexible model ensures you
          can start easily and upgrade as your content needs grow.
        </p>
      ),
    },
    {
      id: 'faq-8',
      question: 'Can anyone else use my AI twin?',
      answer: (
        <p>
          No. Your AI twin is securely linked to your FalcoCut account and cannot be
          accessed by others without your permission. All uploaded photos, videos, and voice
          recordings remain private and protected within your workspace. Team members can
          only use your AI twin if you explicitly grant them access through collaboration
          settings. This ensures your identity and likeness remain fully under your control.
        </p>
      ),
    },
    {
      id: 'faq-9',
      question: 'What affects the quality of my AI twin?',
      answer: (
        <ul className="list-disc list-inside space-y-2">
          <li>
            The quality of your AI twin depends heavily on the clarity, lighting, and angle
            of your uploaded photo or video.
          </li>
          <li>
            Even lighting, minimal shadows, and a front-facing camera position help the AI
            capture your features accurately.
          </li>
          <li>
            High-resolution images produce more realistic facial details and smoother
            lip-sync performance in generated videos.
          </li>
          <li>
            Recording in a clean environment without motion or clutter also improves the
            final result.
          </li>
        </ul>
      ),
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
